/**
 * Location Service — Foreground GPS capture for clock in/out
 * 
 * Captures device coordinates when worker clocks in or out.
 * No background tracking — only captures at explicit user action.
 * Respects company settings feature toggle.
 */

import { isFeatureEnabled } from './settings';

/**
 * Check if location tracking is enabled for this company.
 */
export const isLocationTrackingEnabled = async () => {
  try {
    return await isFeatureEnabled('locationTrackingEnabled');
  } catch (e) {
    return false;
  }
};

/**
 * Request geolocation permission and get current position.
 * Returns { lat, lng, accuracy, timestamp } or null if unavailable.
 */
export const getCurrentPosition = async () => {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      console.warn('[Location] Geolocation not supported');
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy, // meters
          timestamp: new Date().toISOString(),
        });
      },
      (error) => {
        console.warn('[Location] Failed to get position:', error.message);
        resolve(null);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  });
};

/**
 * Build a Google Maps URL from coordinates.
 */
export const getMapUrl = (lat, lng) => {
  if (lat == null || lng == null) return null;
  return `https://www.google.com/maps?q=${lat},${lng}`;
};

/**
 * Format coordinates for display.
 */
export const formatCoordinates = (lat, lng) => {
  if (lat == null || lng == null) return null;
  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
};

/**
 * Get a human-readable location string (lat/lng ± accuracy).
 */
export const formatLocationSummary = (location) => {
  if (!location) return 'Location unavailable';
  const { lat, lng, accuracy } = location;
  const coords = formatCoordinates(lat, lng);
  const accuracyText = accuracy ? `±${Math.round(accuracy)}m` : '';
  return `${coords} ${accuracyText}`.trim();
};
